import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { useMutation, useQuery } from '@apollo/client';
import { insertJeopardyQuizConfig, upsertJeopardyQuizConfig } from 'graphql/mutations';
import { getJeopardyQuizConfig } from 'graphql/queries';
import { useSnackbar } from 'components';
import { getJeopardyThemeVars } from './jeopardy-theme';
import {
    generateJeopardyClues,
    JeopardyDifficulty,
    JeopardyGenerateApiError,
} from './jeopardy-ai-api';
import {
    JeopardyBoardConfig,
    JeopardyCategory,
    JeopardyClue,
    JeopardyFinalRound,
} from './jeopardy-types';
import { getJeopardyBoardConfigValidationErrors } from './jeopardy-config-validation';

interface Props {
    quizId: number;
}

const BLANK_FINAL: JeopardyFinalRound = { category: '', question: '', answer: '' };
const MIN_BOARD_SIZE = 2;
const MAX_BOARD_SIZE = 8;
const DEFAULT_GENERATOR_DIFFICULTY: JeopardyDifficulty = 'Medium';
const WARNING_PREVIEW_COUNT = 3;

type GeneratorType = 'single' | 'double';
type GeneratorPanel = { type: GeneratorType; colIdx: number } | null;

function getGeneratorKey(type: GeneratorType, colIdx: number): string {
    return `${type}-${colIdx}`;
}

function clampBoardSize(value: number) {
    return Math.max(MIN_BOARD_SIZE, Math.min(MAX_BOARD_SIZE, value));
}

function parseBoardSize(value: string, fallback: number) {
    const parsedValue = Number.parseInt(value, 10);

    if (Number.isNaN(parsedValue)) {
        return fallback;
    }

    return clampBoardSize(parsedValue);
}

function makeCategory(prefix: string, colIdx: number, rowsCount: number, multiplier: number): JeopardyCategory {
    return {
        id: `${prefix}-cat-${colIdx}-${Date.now()}`,
        name: `Category ${colIdx + 1}`,
        clues: Array.from({ length: rowsCount }, (_, r) => ({
            id: `${prefix}-clue-${colIdx}-${r}-${Date.now()}`,
            points: (r + 1) * multiplier,
            question: '',
            answer: '',
            difficulty: (r < 2 ? 'Easy' : r < 4 ? 'Medium' : 'Hard') as JeopardyClue['difficulty'],
        })),
    };
}

function syncCategories(
    prev: JeopardyCategory[],
    colsCount: number,
    rowsCount: number,
    prefix: string,
    multiplier: number
): JeopardyCategory[] {
    let updated = [...prev];
    if (updated.length < colsCount) {
        for (let i = updated.length; i < colsCount; i++) {
            updated.push(makeCategory(prefix, i, rowsCount, multiplier));
        }
    } else if (updated.length > colsCount) {
        updated = updated.slice(0, colsCount);
    }
    return updated.map((cat, catIdx) => {
        let clues = [...cat.clues];
        if (clues.length < rowsCount) {
            for (let r = clues.length; r < rowsCount; r++) {
                clues.push({
                    id: `${prefix}-clue-${catIdx}-${r}-${Date.now()}`,
                    points: (r + 1) * multiplier,
                    question: '',
                    answer: '',
                    difficulty: (r < 2 ? 'Easy' : r < 4 ? 'Medium' : 'Hard') as JeopardyClue['difficulty'],
                });
            }
        } else if (clues.length > rowsCount) {
            clues = clues.slice(0, rowsCount);
        }
        return {
            ...cat,
            clues: clues.map((clue, r) => ({ ...clue, points: (r + 1) * multiplier })),
        };
    });
}

export const JeopardyQuizCreator = ({ quizId }: Props) => {
    const snackbar = useSnackbar();
    const theme = useTheme();
    const jeopardyThemeVars = React.useMemo(() => getJeopardyThemeVars(theme), [theme]);

    const [colsCount, setColsCount] = useState(4);
    const [rowsCount, setRowsCount] = useState(5);
    const [colsInput, setColsInput] = useState('4');
    const [rowsInput, setRowsInput] = useState('5');
    const [hasDoubleJeopardy, setHasDoubleJeopardy] = useState(true);
    const [activeTab, setActiveTab] = useState<'single' | 'double' | 'final'>('single');
    const [singleCategories, setSingleCategories] = useState<JeopardyCategory[]>([]);
    const [doubleCategories, setDoubleCategories] = useState<JeopardyCategory[]>([]);
    const [finalJeopardy, setFinalJeopardy] = useState<JeopardyFinalRound>(BLANK_FINAL);
    const [dailyDoubleClueIds, setDailyDoubleClueIds] = useState<string[]>([]);
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);
    const [validationWarnings, setValidationWarnings] = useState<string[]>([]);
    const [generatorTopics, setGeneratorTopics] = useState<Record<string, string>>({});
    const [generatorDifficulties, setGeneratorDifficulties] = useState<
        Record<string, JeopardyDifficulty>
    >({});
    const [activeGeneratorPanel, setActiveGeneratorPanel] =
        useState<GeneratorPanel>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationError, setGenerationError] = useState<string | null>(null);
    const [warningsDismissed, setWarningsDismissed] = useState(false);
    const [warningsExpanded, setWarningsExpanded] = useState(false);

    // Load existing config from DB
    const {
        data: configData,
        loading: configLoading,
        refetch: refetchConfig,
    } = useQuery(getJeopardyQuizConfig, {
        variables: { quizId },
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        const rows = configData?.quiz_master_quiz_settings;
        if (rows && rows.length > 0 && rows[0].additional_config) {
            const cfg: JeopardyBoardConfig = rows[0].additional_config;
            setColsCount(cfg.columnsCount);
            setRowsCount(cfg.rowsCount);
            setHasDoubleJeopardy(cfg.hasDoubleJeopardy);
            setSingleCategories(cfg.jeopardyCategories ?? []);
            setDoubleCategories(cfg.doubleJeopardyCategories ?? []);
            setFinalJeopardy(cfg.finalJeopardy ?? BLANK_FINAL);
            setDailyDoubleClueIds(cfg.dailyDoubleClueIds ?? []);
        }
    }, [configData]);

    // Sync board structure when dimensions change
    useEffect(() => {
        setSingleCategories(prev => syncCategories(prev, colsCount, rowsCount, 'single', 200));
        if (hasDoubleJeopardy) {
            setDoubleCategories(prev => syncCategories(prev, colsCount, rowsCount, 'double', 400));
        }
    }, [colsCount, rowsCount, hasDoubleJeopardy]);

    const [upsertConfig, { loading: updating }] = useMutation(upsertJeopardyQuizConfig);
    const [insertConfig, { loading: inserting }] = useMutation(insertJeopardyQuizConfig);
    const saving = updating || inserting;

    const buildConfig = (): JeopardyBoardConfig => ({
        columnsCount: colsCount,
        rowsCount,
        hasDoubleJeopardy,
        dailyDoubleClueIds,
        jeopardyCategories: singleCategories,
        doubleJeopardyCategories: hasDoubleJeopardy ? doubleCategories : [],
        finalJeopardy,
    });

    const validate = (): string[] => getJeopardyBoardConfigValidationErrors(buildConfig());

    useEffect(() => {
        if (attemptedSubmit) setValidationWarnings(validate());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [singleCategories, doubleCategories, finalJeopardy, attemptedSubmit]);

    useEffect(() => {
        setColsInput(String(colsCount));
    }, [colsCount]);

    useEffect(() => {
        setRowsInput(String(rowsCount));
    }, [rowsCount]);

    useEffect(() => {
        if (!hasDoubleJeopardy && activeTab === 'double') {
            setActiveTab('single');
        }

        if (!hasDoubleJeopardy && activeGeneratorPanel?.type === 'double') {
            setActiveGeneratorPanel(null);
        }
    }, [activeGeneratorPanel, activeTab, hasDoubleJeopardy]);

    const handleSave = async () => {
        setAttemptedSubmit(true);
        const warnings = validate();
        setValidationWarnings(warnings);
        setWarningsDismissed(false);
        setWarningsExpanded(false);

        const config = buildConfig();
        try {
            const updateResult = await upsertConfig({
                variables: { quizId, jeopardyConfig: config },
            });

            const affectedRows =
                updateResult.data?.update_quiz_master_quiz_settings?.affected_rows ?? 0;

            if (affectedRows === 0) {
                await insertConfig({ variables: { quizId, jeopardyConfig: config } });
            }

            if (warnings.length > 0) {
                snackbar.success(
                    `Jeopardy board saved with ${warnings.length} warning${warnings.length === 1 ? '' : 's'}.`
                );
            } else {
                snackbar.success('Jeopardy board saved!');
            }
            void refetchConfig();
        } catch (error) {
            const message =
                error instanceof Error ? error.message : 'Unknown save error';
            snackbar.error('Failed to save board: ' + message);
        }
    };

    const handleCategoryName = (type: 'single' | 'double', colIdx: number, name: string) => {
        const setter = type === 'single' ? setSingleCategories : setDoubleCategories;
        setter(prev => {
            const next = [...prev];
            next[colIdx] = { ...next[colIdx], name };
            return next;
        });
    };

    const handleClueChange = (
        type: 'single' | 'double',
        colIdx: number,
        rowIdx: number,
        field: keyof Pick<JeopardyClue, 'question' | 'answer' | 'difficulty'>,
        value: string
    ) => {
        const setter = type === 'single' ? setSingleCategories : setDoubleCategories;
        setter(prev => {
            const next = [...prev];
            const cat = { ...next[colIdx] };
            const clues = [...cat.clues];
            clues[rowIdx] = { ...clues[rowIdx], [field]: value };
            cat.clues = clues;
            next[colIdx] = cat;
            return next;
        });
    };

    const toggleDailyDouble = (clueId: string) => {
        setDailyDoubleClueIds(prev =>
            prev.includes(clueId) ? prev.filter(id => id !== clueId) : [...prev, clueId]
        );
    };

    const handleBoardSizeInputChange = (
        value: string,
        setter: React.Dispatch<React.SetStateAction<string>>
    ) => {
        setter(value.replace(/\D/g, ''));
    };

    const commitBoardSizeInput = (
        value: string,
        fallback: number,
        countSetter: React.Dispatch<React.SetStateAction<number>>,
        inputSetter: React.Dispatch<React.SetStateAction<string>>
    ) => {
        const nextValue = parseBoardSize(value, fallback);
        countSetter(nextValue);
        inputSetter(String(nextValue));
    };

    const handleBoardSizeKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>,
        value: string,
        fallback: number,
        countSetter: React.Dispatch<React.SetStateAction<number>>,
        inputSetter: React.Dispatch<React.SetStateAction<string>>
    ) => {
        if (event.key === 'Enter') {
            commitBoardSizeInput(value, fallback, countSetter, inputSetter);
            event.currentTarget.blur();
        }
    };

    const toggleGeneratorPanel = (
        type: GeneratorType,
        colIdx: number,
        categoryName: string,
    ) => {
        const isSamePanel =
            activeGeneratorPanel?.type === type &&
            activeGeneratorPanel.colIdx === colIdx;

        if (isSamePanel) {
            setActiveGeneratorPanel(null);
            return;
        }

        const key = getGeneratorKey(type, colIdx);
        setGeneratorTopics(prev => ({
            ...prev,
            [key]: prev[key] ?? categoryName,
        }));
        setGeneratorDifficulties(prev => ({
            ...prev,
            [key]: prev[key] ?? DEFAULT_GENERATOR_DIFFICULTY,
        }));
        setActiveGeneratorPanel({ type, colIdx });
    };

    const handleAiCategoryGeneration = async (
        type: GeneratorType,
        colIdx: number,
    ) => {
        setIsGenerating(true);
        setGenerationError(null);

        const categories = type === 'single' ? singleCategories : doubleCategories;
        const targetCategory = categories[colIdx];
        const key = getGeneratorKey(type, colIdx);
        const topic =
            generatorTopics[key]?.trim() ||
            targetCategory?.name?.trim() ||
            'Random Trivia';
        const difficulty =
            generatorDifficulties[key] ?? DEFAULT_GENERATOR_DIFFICULTY;

        try {
            const generated = await generateJeopardyClues({
                category: topic,
                difficulty,
                count: rowsCount,
            });

            const previousClueIds = new Set(
                (targetCategory?.clues ?? []).map(clue => clue.id),
            );

            const mappedClues = generated.clues.map((clue, rowIdx) => ({
                id: `${type}-clue-${colIdx}-${rowIdx}-${Date.now()}`,
                points: (rowIdx + 1) * (type === 'single' ? 200 : 400),
                question: clue.question,
                answer: clue.answer,
                difficulty: clue.difficulty,
            }));

            if (type === 'single') {
                setSingleCategories(prev => {
                    const next = [...prev];
                    next[colIdx] = {
                        ...next[colIdx],
                        name: topic,
                        clues: mappedClues,
                    };
                    return next;
                });
            } else {
                setDoubleCategories(prev => {
                    const next = [...prev];
                    next[colIdx] = {
                        ...next[colIdx],
                        name: topic,
                        clues: mappedClues,
                    };
                    return next;
                });
            }

            setDailyDoubleClueIds(prev =>
                prev.filter(clueId => !previousClueIds.has(clueId)),
            );

            setActiveGeneratorPanel(null);
            snackbar.success(`Generated ${rowsCount} clues for "${topic}".`);
        } catch (error) {
            const fallbackMessage = 'Failed to generate AI clues.';
            const message =
                error instanceof JeopardyGenerateApiError
                    ? error.message
                    : fallbackMessage;

            setGenerationError(message);
            snackbar.error(message);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleAiFinalJeopardyGeneration = async () => {
        setIsGenerating(true);
        setGenerationError(null);

        const topic = finalJeopardy.category.trim() || 'Famous Landmarks';

        try {
            const generated = await generateJeopardyClues({
                category: topic,
                difficulty: 'Hard',
                count: 1,
            });

            const generatedClue = generated.clues[0];
            if (!generatedClue) {
                throw new Error('No final clue was returned.');
            }

            setFinalJeopardy({
                category: topic,
                question: generatedClue.question,
                answer: generatedClue.answer,
            });

            snackbar.success(`Generated Final Jeopardy clue for "${topic}".`);
        } catch (error) {
            const fallbackMessage = 'Failed to generate Final Jeopardy clue.';
            const message =
                error instanceof JeopardyGenerateApiError
                    ? error.message
                    : fallbackMessage;

            setGenerationError(message);
            snackbar.error(message);
        } finally {
            setIsGenerating(false);
        }
    };

    const currentCategories = activeTab === 'single' ? singleCategories : doubleCategories;
    const currentType = activeTab === 'single' ? 'single' : 'double';
    const shouldShowWarnings = attemptedSubmit && validationWarnings.length > 0;
    const visibleWarnings = warningsExpanded
        ? validationWarnings
        : validationWarnings.slice(0, WARNING_PREVIEW_COUNT);
    const hiddenWarningCount = Math.max(validationWarnings.length - visibleWarnings.length, 0);

    if (configLoading) {
        return <div className="jeopardy-creator-loading">Loading board configuration...</div>;
    }

    return (
        <div className="jeopardy-creator" style={jeopardyThemeVars}>
            <div className="jeopardy-creator__header">
                <h3>Jeopardy Board Configurator</h3>
                <p className="jeopardy-creator__subtitle">
                    Configure categories and clues for this Jeopardy quiz. The board is saved to the database
                    and reused for every play session.
                </p>
            </div>

            {/* Board dimensions */}
            <div className="jeopardy-creator__settings">
                <div className="jeopardy-creator__setting-row">
                    <label>Columns (categories per stage)</label>
                    <div className="jeopardy-creator__stepper">
                        <button
                            type="button"
                            className="jeopardy-creator__stepper-button"
                            aria-label="Decrease columns"
                            disabled={colsCount <= MIN_BOARD_SIZE}
                            onClick={() => setColsCount(prev => clampBoardSize(prev - 1))}
                        >
                            −
                        </button>
                        <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={colsInput}
                            aria-label="Columns"
                            onChange={e => handleBoardSizeInputChange(e.target.value, setColsInput)}
                            onBlur={() => commitBoardSizeInput(colsInput, colsCount, setColsCount, setColsInput)}
                            onKeyDown={e => handleBoardSizeKeyDown(e, colsInput, colsCount, setColsCount, setColsInput)}
                        />
                        <button
                            type="button"
                            className="jeopardy-creator__stepper-button"
                            aria-label="Increase columns"
                            disabled={colsCount >= MAX_BOARD_SIZE}
                            onClick={() => setColsCount(prev => clampBoardSize(prev + 1))}
                        >
                            +
                        </button>
                    </div>
                </div>
                <div className="jeopardy-creator__setting-row">
                    <label>Rows (clues per category)</label>
                    <div className="jeopardy-creator__stepper">
                        <button
                            type="button"
                            className="jeopardy-creator__stepper-button"
                            aria-label="Decrease rows"
                            disabled={rowsCount <= MIN_BOARD_SIZE}
                            onClick={() => setRowsCount(prev => clampBoardSize(prev - 1))}
                        >
                            −
                        </button>
                        <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={rowsInput}
                            aria-label="Rows"
                            onChange={e => handleBoardSizeInputChange(e.target.value, setRowsInput)}
                            onBlur={() => commitBoardSizeInput(rowsInput, rowsCount, setRowsCount, setRowsInput)}
                            onKeyDown={e => handleBoardSizeKeyDown(e, rowsInput, rowsCount, setRowsCount, setRowsInput)}
                        />
                        <button
                            type="button"
                            className="jeopardy-creator__stepper-button"
                            aria-label="Increase rows"
                            disabled={rowsCount >= MAX_BOARD_SIZE}
                            onClick={() => setRowsCount(prev => clampBoardSize(prev + 1))}
                        >
                            +
                        </button>
                    </div>
                </div>
                <div className="jeopardy-creator__setting-row">
                    <label>
                        <input
                            type="checkbox"
                            checked={hasDoubleJeopardy}
                            onChange={e => setHasDoubleJeopardy(e.target.checked)}
                        />
                        {' '}Enable Double Jeopardy stage
                    </label>
                </div>
            </div>

            {/* Stage tabs */}
            <div className="jeopardy-creator__tabs">
                <button
                    className={`jeopardy-creator__tab${activeTab === 'single' ? ' jeopardy-creator__tab--active' : ''}`}
                    onClick={() => setActiveTab('single')}
                >
                    Jeopardy
                </button>
                {hasDoubleJeopardy && (
                    <button
                        className={`jeopardy-creator__tab${activeTab === 'double' ? ' jeopardy-creator__tab--active' : ''}`}
                        onClick={() => setActiveTab('double')}
                    >
                        Double Jeopardy
                    </button>
                )}
                <button
                    className={`jeopardy-creator__tab${activeTab === 'final' ? ' jeopardy-creator__tab--active' : ''}`}
                    onClick={() => setActiveTab('final')}
                >
                    Final Jeopardy
                </button>
            </div>

            {/* Validation warnings */}
            {shouldShowWarnings && !warningsDismissed && (
                <div id="validation_summary" className="jeopardy-creator__errors jeopardy-creator__errors--warning">
                    <div className="jeopardy-creator__warning-header-row">
                        <strong>
                            Board saved with {validationWarnings.length} warning{validationWarnings.length === 1 ? '' : 's'}. Fix these before starting a Jeopardy session.
                        </strong>
                        <div className="jeopardy-creator__warning-actions">
                            {validationWarnings.length > WARNING_PREVIEW_COUNT && (
                                <button
                                    type="button"
                                    className="jeopardy-creator__warning-link"
                                    onClick={() => setWarningsExpanded(prev => !prev)}
                                >
                                    {warningsExpanded ? 'Show less' : 'Show all'}
                                </button>
                            )}
                            <button
                                type="button"
                                className="jeopardy-creator__warning-link"
                                onClick={() => setWarningsDismissed(true)}
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                    <ul>
                        {visibleWarnings.map((err, i) => <li key={i}>{err}</li>)}
                    </ul>
                    {!warningsExpanded && hiddenWarningCount > 0 && (
                        <div className="jeopardy-creator__warning-more">
                            {hiddenWarningCount} more warning{hiddenWarningCount === 1 ? '' : 's'} hidden.
                        </div>
                    )}
                </div>
            )}

            {shouldShowWarnings && warningsDismissed && (
                <div className="jeopardy-creator__warning-chip-row">
                    <button
                        type="button"
                        className="jeopardy-creator__warning-chip"
                        onClick={() => setWarningsDismissed(false)}
                    >
                        Show {validationWarnings.length} warning{validationWarnings.length === 1 ? '' : 's'}
                    </button>
                </div>
            )}

            {generationError && (
                <div className="jeopardy-creator__errors jeopardy-creator__errors--warning">
                    <strong>AI generation failed:</strong>
                    <div>{generationError}</div>
                </div>
            )}

            {/* Board editor for single/double stages */}
            {activeTab !== 'final' && (
                <div className="jeopardy-creator__board">
                    {currentCategories.map((cat, colIdx) => (
                        <div key={cat.id} className="jeopardy-creator__column">
                            <div className="jeopardy-creator__category-header">
                                <input
                                    type="text"
                                    placeholder={`Category ${colIdx + 1}`}
                                    value={cat.name}
                                    onChange={e => handleCategoryName(currentType, colIdx, e.target.value)}
                                    className={attemptedSubmit && !cat.name.trim() ? 'input-error' : ''}
                                />
                                <div className="jeopardy-creator__generator-toggle-row">
                                    <button
                                        type="button"
                                        className="jeopardy-creator__generator-toggle"
                                        onClick={() =>
                                            toggleGeneratorPanel(
                                                currentType,
                                                colIdx,
                                                cat.name,
                                            )
                                        }
                                    >
                                        {activeGeneratorPanel?.type === currentType &&
                                        activeGeneratorPanel.colIdx === colIdx
                                            ? 'Hide AI Generator'
                                            : 'AI Generate Category'}
                                    </button>
                                </div>
                                {activeGeneratorPanel?.type === currentType &&
                                    activeGeneratorPanel.colIdx === colIdx && (
                                        <div className="jeopardy-creator__generator-panel">
                                            <label>Topic</label>
                                            <input
                                                type="text"
                                                value={
                                                    generatorTopics[
                                                        getGeneratorKey(
                                                            currentType,
                                                            colIdx,
                                                        )
                                                    ] ?? ''
                                                }
                                                onChange={e =>
                                                    setGeneratorTopics(prev => ({
                                                        ...prev,
                                                        [getGeneratorKey(
                                                            currentType,
                                                            colIdx,
                                                        )]: e.target.value,
                                                    }))
                                                }
                                                placeholder="e.g. Astronomy"
                                            />

                                            <label>Difficulty</label>
                                            <select
                                                value={
                                                    generatorDifficulties[
                                                        getGeneratorKey(
                                                            currentType,
                                                            colIdx,
                                                        )
                                                    ] ??
                                                    DEFAULT_GENERATOR_DIFFICULTY
                                                }
                                                onChange={e =>
                                                    setGeneratorDifficulties(prev => ({
                                                        ...prev,
                                                        [getGeneratorKey(
                                                            currentType,
                                                            colIdx,
                                                        )]: e.target
                                                            .value as JeopardyDifficulty,
                                                    }))
                                                }
                                            >
                                                <option value="Easy">Easy</option>
                                                <option value="Medium">Medium</option>
                                                <option value="Hard">Hard</option>
                                            </select>

                                            <button
                                                type="button"
                                                className="jeopardy-creator__generator-action"
                                                disabled={isGenerating}
                                                onClick={() =>
                                                    handleAiCategoryGeneration(
                                                        currentType,
                                                        colIdx,
                                                    )
                                                }
                                            >
                                                {isGenerating
                                                    ? 'Generating...'
                                                    : `Generate ${rowsCount} clues`}
                                            </button>
                                        </div>
                                    )}
                            </div>
                            {cat.clues.map((clue, rowIdx) => {
                                const isDD = dailyDoubleClueIds.includes(clue.id);
                                return (
                                <div key={clue.id} className={`jeopardy-creator__clue${isDD ? ' --daily-double' : ''}`}>
                                    <div className="jeopardy-creator__clue-header">
                                        <span className="jeopardy-creator__clue-points">${clue.points}</span>
                                        <button
                                            type="button"
                                            className={`jeopardy-creator__clue-dd-toggle${isDD ? ' --active' : ''}`}
                                            onClick={() => toggleDailyDouble(clue.id)}
                                            title={isDD ? 'Remove Daily Double' : 'Mark as Daily Double'}
                                        >
                                            {isDD ? '⭐ DD' : 'DD'}
                                        </button>
                                    </div>
                                    <textarea
                                        placeholder="Question / Clue"
                                        value={clue.question}
                                        rows={2}
                                        onChange={e => handleClueChange(currentType, colIdx, rowIdx, 'question', e.target.value)}
                                        className={attemptedSubmit && !clue.question.trim() ? 'input-error' : ''}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Answer"
                                        value={clue.answer}
                                        onChange={e => handleClueChange(currentType, colIdx, rowIdx, 'answer', e.target.value)}
                                        className={attemptedSubmit && !clue.answer.trim() ? 'input-error' : ''}
                                    />
                                    <select
                                        value={clue.difficulty}
                                        onChange={e => handleClueChange(currentType, colIdx, rowIdx, 'difficulty', e.target.value)}
                                    >
                                        <option value="Easy">Easy</option>
                                        <option value="Medium">Medium</option>
                                        <option value="Hard">Hard</option>
                                    </select>
                                </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}

            {/* Final Jeopardy editor */}
            {activeTab === 'final' && (
                <div className="jeopardy-creator__final">
                    <div className="jeopardy-creator__final-field">
                        <div className="jeopardy-creator__final-field-label-row">
                            <label>Final Jeopardy Category</label>
                            <button
                                type="button"
                                className="jeopardy-creator__generator-toggle"
                                disabled={isGenerating}
                                onClick={handleAiFinalJeopardyGeneration}
                            >
                                {isGenerating
                                    ? 'Generating...'
                                    : 'AI Auto-fill Final'}
                            </button>
                        </div>
                        <input
                            type="text"
                            value={finalJeopardy.category}
                            onChange={e => setFinalJeopardy(f => ({ ...f, category: e.target.value }))}
                            placeholder="e.g. World History"
                            className={attemptedSubmit && !finalJeopardy.category.trim() ? 'input-error' : ''}
                        />
                    </div>
                    <div className="jeopardy-creator__final-field">
                        <label>Final Jeopardy Question / Clue</label>
                        <textarea
                            rows={3}
                            value={finalJeopardy.question}
                            onChange={e => setFinalJeopardy(f => ({ ...f, question: e.target.value }))}
                            placeholder="Write the final clue here..."
                            className={attemptedSubmit && !finalJeopardy.question.trim() ? 'input-error' : ''}
                        />
                    </div>
                    <div className="jeopardy-creator__final-field">
                        <label>Final Jeopardy Answer</label>
                        <input
                            type="text"
                            value={finalJeopardy.answer}
                            onChange={e => setFinalJeopardy(f => ({ ...f, answer: e.target.value }))}
                            placeholder="The correct answer"
                            className={attemptedSubmit && !finalJeopardy.answer.trim() ? 'input-error' : ''}
                        />
                    </div>
                </div>
            )}

            {/* Save action */}
            <div className="jeopardy-creator__actions">
                <button
                    className="jeopardy-creator__save-btn"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Save Board Configuration'}
                </button>
            </div>
        </div>
    );
};
